<script setup lang="ts">
import { computed } from "vue"
import AppIcon from "@/components/shared/AppIcon.vue"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"
import type { UserMenuItem } from "@/types/navigation"

/**
 * SidebarLayoutUserMenu.vue is a supporting component for SidebarLayout.vue
 * and is not intended to be imported directly.
 */
const props = withDefaults(
  defineProps<{
    menu?: UserMenuItem[]
  }>(),
  {
    menu: () => [],
  }
)

const items = computed(() => {
  return props.menu
    .map((item) => {
      if (item.action === "nav") {
        return {
          ...item,
          navigation: item.navigation.filter((subItem) => subItem.show),
        }
      }

      return item
    })
    .filter((item) => {
      if (item.action === "nav") {
        return item.show && item.navigation.length > 0
      }

      return item.show
    })
})
</script>

<template>
  <div
    v-if="items.length > 0"
    class="flex items-center justify-center gap-3 relative"
  >
    <template v-for="(item, key) in items" :key="key">
      <div v-if="item.action === 'url'" v-tooltip:bottom-end="item.name">
        <a
          :href="item.url"
          class="block focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-white/80 rounded-md"
        >
          <AppIcon
            bg-color="bg-black/20"
            :activity-badge="item.activityBadge"
            :activity-badge-max="item.activityBadgeMax"
            icon-color="text-neutral-200"
            :icon="item.icon"
            size="base"
            type="squircle"
            >{{ item.name }}</AppIcon
          >
        </a>
      </div>

      <div v-else-if="item.action === 'nav'" class="cursor-pointer sm:relative">
        <Menu v-slot="{ open }: { open: boolean }" as="template">
          <MenuButton
            class="focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-white/80 rounded-md relative"
          >
            <AppIcon
              :activity-badge="item.activityBadge"
              :activity-badge-max="item.activityBadgeMax"
              bg-color="bg-black/20"
              icon-color="text-neutral-200"
              :icon="item.icon"
              size="base"
              type="squircle"
              >{{ item.name }}</AppIcon
            >
            <!--NOTE(spk): toggle the visibility of the tooltip when the menu is open-->
            <div
              v-if="!open"
              v-tooltip:bottom-end="item.name"
              class="inset-0 absolute"
            />
          </MenuButton>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems
              class="origin-top-right absolute right-0 mt-2 min-w-max focus:outline-none focus:ring-0"
            >
              <div class="max-w-72 min-w-48 rounded-xl shadow-lg p-2 bg-white">
                <MenuItem
                  v-for="subItem in item.navigation"
                  :key="subItem.name"
                  v-slot="{ active }: { active: boolean }"
                >
                  <a
                    :href="subItem.url"
                    :target="subItem?.openInTab ? '_blank' : null"
                    :class="[
                      'block relative p-3 text-sm/6 rounded-md',
                      active && 'bg-gray-100',
                    ]"
                  >
                    <div class="font-semibold text-gray-900">
                      {{ subItem.name }}
                    </div>

                    <div
                      v-if="subItem.description"
                      class="mt-0.5 text-gray-600"
                    >
                      {{ subItem.description }}
                    </div>
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </template>
  </div>
</template>
